import type { ContractRequest } from '@/types/contractRequests'
import { ArchivedContractStatuses, ContractStatus, InProgressContractStatuses, Status } from '@/types/contractStatuses'
import type { Contract } from '@/types/contracts'
import { brokerIsStroobants } from '@/utils/brokers'
import { filterStroobantsClaims } from '@/utils/claims'
import { filterContractsWithoutLifePaidUpStopped, lifeContractsTerminatedWithPaidUpReason } from '@/utils/contracts'
import { RequestStatus, handleRequest, initialRequestState } from '@/utils/restStore'
import type { RequestState } from '@/utils/restStore'
import { defineStore } from 'pinia'

import Api from '@/services/api'

interface State {
  contracts: RequestState<Contract[]>
  contractRequests: RequestState<ContractRequest[]>
  selectedStatus: Status
  terminatedLifeContractsWithPaidUpReason: Contract[]
}

const initialState = (): State => ({
  contracts: initialRequestState(),
  terminatedLifeContractsWithPaidUpReason: [],
  contractRequests: initialRequestState(),
  selectedStatus: Status.InProgress
})

export const useContractsStore = defineStore({
  id: 'contracts',
  state: initialState,
  actions: {
    selectStatus(status: Status) {
      this.selectedStatus = status
    },
    async fetchContracts() {
      //* fetch contracts(status : pending_for_... & active & archived)
      await handleRequest(Api.getContracts, (contracts: RequestState<Contract[]>) => {
        this.contracts = contracts
        if (this.contracts.status === RequestStatus.RequestLoaded) {
          this.terminatedLifeContractsWithPaidUpReason = lifeContractsTerminatedWithPaidUpReason(this.contracts.data)
          this.contracts.data = filterContractsWithoutLifePaidUpStopped(this.contracts.data)
        }
        if (this.contracts.status === RequestStatus.RequestLoaded && brokerIsStroobants()) {
          this.contracts.data = filterContractsWithoutLifePaidUpStopped(this.contracts.data).map((contract) => ({
            ...contract,
            claims: filterStroobantsClaims(contract.claims)
          }))
        }
      })
      await handleRequest(Api.getContractRequests, (contractRequests: RequestState<ContractRequest[]>) => {
        this.contractRequests = contractRequests
      })

      if (this.countContractsByStatus(Status.InProgress) === 0 && this.countContractsByStatus(Status.Pending) > 0)
        this.selectedStatus = Status.Pending
      else this.selectedStatus = Status.InProgress
    },
    async fetchCompanyContracts(companyId: string) {
      await handleRequest(
        () => Api.getCompanyContracts(companyId),
        (contracts: RequestState<Contract[]>) => {
          this.contracts = contracts
          this.contractRequests = {
            status: RequestStatus.RequestLoaded,
            data: []
          }

          if (this.contracts.status === RequestStatus.RequestLoaded && brokerIsStroobants()) {
            this.contracts.data = this.contracts.data.map((contract) => ({
              ...contract,
              claims: filterStroobantsClaims(contract.claims)
            }))
          }

          if (this.countContractsByStatus(Status.InProgress) === 0 && this.countContractsByStatus(Status.Pending) > 0)
            this.selectedStatus = Status.Pending
          else if (
            this.countContractsByStatus(Status.InProgress) === 0 &&
            this.countContractsByStatus(Status.Archived) > 0
          )
            this.selectedStatus = Status.Archived
          else this.selectedStatus = Status.InProgress
        }
      )
    }
  },
  getters: {
    contractsFilteredByStatus: (state: State): (Contract | ContractRequest)[] => {
      const contractsFiltered = contractsByStatus(state, state.selectedStatus).concat(
        contractRequestsByStatus(state, state.selectedStatus)
      )
      return contractsFiltered.sort(function (a, b) {
        if (a.productType < b.productType) return -1
        if (a.productType > b.productType) return 1
        return 0
      })
    },
    countContractsByStatus:
      (state: State) =>
      (status: Status): number =>
        contractRequestsByStatus(state, status).length + contractsByStatus(state, status).length,
    countContractsWithPendingActions: (state: State): number => {
      const count = contractsByStatus(state, Status.Pending)
        .concat(contractRequestsByStatus(state, Status.Pending))
        .filter((contract) => contract.missingDocumentsCount).length
      return count
    }
  }
})

// return contracts by status
const contractsByStatus = (state: State, status: Status): (Contract | ContractRequest)[] => {
  let contracts: (Contract | ContractRequest)[] = []
  if (state.contracts.status === RequestStatus.RequestLoaded) {
    contracts = state.contracts.data.filter((contract) => contractStatuses(status).includes(contract.status))
    // for pending status : add contract with status inProgress and pending signature
    if (status === Status.Pending)
      contracts = contracts.concat(
        state.contracts.data.filter((contract) => contract.pendingSignature && contract.status === 'in_progress')
      )
    // for inProgress status : remove contract with pending signature
    if (status === Status.InProgress)
      contracts = contracts.filter((contract) => !('pendingSignature' in contract && contract.pendingSignature))
  }
  return contracts
}

// return contractsRequests by status
const contractRequestsByStatus = (state: State, status: Status): (Contract | ContractRequest)[] =>
  state.contractRequests.status === RequestStatus.RequestLoaded
    ? state.contractRequests.data.filter((contract) => contractStatuses(status).includes(contract.status))
    : []

const contractStatuses = (selectedStatus: Status): ContractStatus[] => {
  switch (selectedStatus) {
    case Status.InProgress:
      return InProgressContractStatuses
    case Status.Pending:
      return [
        ContractStatus.WaitingForSignature,
        ContractStatus.WaitingForDetails,
        ContractStatus.WaitingForDetailsAndVerification,
        ContractStatus.WaitingForConfirmation,
        ContractStatus.WaitingForPayment,
        ContractStatus.WaitingForValidation,
        ContractStatus.WaitingForValidationAndVerification,
        ContractStatus.WaitingForVerification
      ]
    case Status.Archived:
      return ArchivedContractStatuses
  }
}
